/**
 * Convex Provider Component with Clerk Authentication (September 2025)
 * 
 * Sets up Convex client with Clerk authentication and provides real-time 
 * functionality to the React app. This replaces the old WebSocket connection 
 * management with Convex's built-in reactive query system.
 * 
 * Reference: https://docs.convex.dev/auth/clerk
 */

import React from 'react';
import { ConvexProvider as ConvexReactProvider } from 'convex/react';
import { ConvexReactClient } from 'convex/react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { getConvexUrl } from '../utils/convex';

// Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (CLERK_PUBLISHABLE_KEY === null || CLERK_PUBLISHABLE_KEY === undefined || CLERK_PUBLISHABLE_KEY === '') {
  throw new Error("Missing Clerk Publishable Key");
}

// Initialize Convex client with WebSocket URL for queries/mutations
// Uses .convex.cloud domain (NOT .convex.site)
const convex = new ConvexReactClient(getConvexUrl());

interface ConvexProviderProps {
  children: React.ReactNode;
}

/**
 * ConvexProviderWithClerk - Integrates Clerk authentication with Convex
 * 
 * This component handles the authentication token passing between Clerk and Convex.
 * It ensures that Convex functions receive the proper authentication context.
 */
function ConvexProviderWithClerk({ children }: ConvexProviderProps) {
  const { isLoaded, getToken } = useAuth();

  // Use React.ComponentType to resolve type compatibility with React 19
  const Provider = ConvexReactProvider as React.ComponentType<{
    client: ConvexReactClient;
    children?: React.ReactNode;
  }>;

  React.useEffect(() => {
    const updateConvexClient = async () => {
      if (isLoaded) {
        convex.setAuth(async () => {
          // Use the "convex" JWT template configured in Clerk dashboard
          // This template has "aud": "convex" claim for proper validation
          return await getToken({ template: "convex" });
        });
      }
    };

    updateConvexClient();
  }, [isLoaded, getToken]);

  return (
    <Provider client={convex}>
      {children}
    </Provider>
  );
}

/**
 * Main ConvexProvider - Wraps both Clerk and Convex providers
 * 
 * This is the complete authentication + real-time data provider.
 * It combines Clerk for authentication with Convex for real-time functionality.
 * 
 * Key benefits:
 * - Automatic real-time updates when data changes
 * - User authentication with Clerk
 * - Secure API access with authenticated Convex functions
 * - No manual WebSocket connection lifecycle management  
 * - Built-in error handling and retry logic
 * - Clean separation from Redux (UI state) and Convex (server state)
 */
export function ConvexProvider({ children }: ConvexProviderProps) {
  // Use React.ComponentType to resolve type compatibility with React 19
  const ClerkProviderCompat = ClerkProvider as React.ComponentType<{
    publishableKey: string;
    appearance?: Record<string, unknown>;
    children?: React.ReactNode;
  }>;

  return (
    <ClerkProviderCompat
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={{
        baseTheme: undefined, // Use default theme
        variables: {
          colorPrimary: '#3b82f6', // Blue-500 for primary actions
          colorBackground: '#ffffff',
          colorText: '#1f2937', // Gray-800 for text
        },
        elements: {
          formButtonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
          card: 'shadow-lg',
        }
      }}
    >
      <ConvexProviderWithClerk>
        {children}
      </ConvexProviderWithClerk>
    </ClerkProviderCompat>
  );
}

/**
 * Usage in main.tsx:
 * 
 * ```typescript
 * import { ConvexProvider } from './components/ConvexProvider';
 * 
 * ReactDOM.createRoot(document.getElementById('root')!).render(
 *   <React.StrictMode>
 *     <ConvexProvider>
 *       <App />
 *     </ConvexProvider>
 *   </React.StrictMode>
 * );
 * ```
 * 
 * Key benefits:
 * - Automatic real-time updates when data changes
 * - No manual WebSocket connection lifecycle management
 * - Built-in error handling and retry logic
 * - Optimal network usage and performance
 * - Clean separation from Redux (UI state) and Convex (server state)
 */